"use client";

import { useState } from "react";
import { Activity, HandHelping, LayoutGrid, PauseCircle, PlayCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { PROJECTS, chairOf, coversLine, health, issues, remindTarget, type ActivityItem, type Group, type GroupType, type Health } from "./data";
import { GroupDetail } from "./GroupDetail";
import { HealthBadge, Stat } from "./ui";

interface HelpItem { id: string; group: string; text: string; action: string }

/** Where staff can step in. Worked out from the committees. */
function helpItems(groups: Group[]): HelpItem[] {
    const out: HelpItem[] = [];
    for (const g of groups) {
        if (g.lastStatus === "Recording missing") out.push({ id: `${g.id}-rec`, group: g.name, text: `Nothing came through from Teams for the ${g.lastMeeting} meeting. ${remindTarget(g)} needs to upload notes.`, action: "Help upload notes" });
        if (g.lastStatus === "Did it happen?") out.push({ id: `${g.id}-held`, group: g.name, text: `No sign the ${g.lastMeeting} meeting took place. ${remindTarget(g)} hasn't answered yet.`, action: "Check with the Chair" });
        if (!g.chair) out.push({ id: `${g.id}-chair`, group: g.name, text: `No Chair in post, so nobody can approve the calendar or confirm minutes. ${g.liaison} (staff liaison) can raise it with the Governance Committee.`, action: "Raise with the Governance Committee" });
        if (g.nextYear === "Not started" && g.planned === 0) out.push({ id: `${g.id}-plan`, group: g.name, text: `No meetings planned at all. The staff liaison can draft a calendar for ${g.chair ?? "a new Chair"} to approve.`, action: "Draft their calendar" });
    }
    out.push({ id: "pub", group: "Governance Committee", text: "One item from the 21 Oct minutes is marked for publishing: “The Governance Committee recommends a one-page group report.”", action: "Approve for publishing" });
    return out;
}

const TYPES: ("All" | GroupType)[] = ["All", "Board", "Board committee", "Committee of the Association"];
const HEALTHS: ("All" | Health)[] = ["All", "On track", "Needs attention", "Off track"];

/** Staff see everything going on in governance. */
export function StaffView({ groups, activity, reminded }: { groups: Group[]; activity: ActivityItem[]; reminded: string[] }) {
    const [type, setType] = useState<(typeof TYPES)[number]>("All");
    const [state, setState] = useState<(typeof HEALTHS)[number]>("All");
    const [open, setOpen] = useState<Group | null>(null);
    const [handled, setHandled] = useState<string[]>([]);
    const [paused, setPaused] = useState({ scheduling: false, recording: false });

    const shown = groups.filter((g) => (type === "All" || g.type === type) && (state === "All" || health(g) === state));
    const planned = groups.filter((g) => g.nextYear === "Approved").length;
    const held = groups.reduce((n, g) => n + g.held, 0);
    const due = groups.reduce((n, g) => n + g.due, 0);
    const onTime = groups.reduce((n, g) => n + g.onTime, 0);
    const reports = groups.filter((g) => g.report).length;
    const help = helpItems(groups);
    const onTrack = groups.filter((g) => health(g) === "On track").length;

    return (
        <div className="space-y-6">
            <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-[#007a8a]">Staff</div>
                <h2 className="text-2xl font-bold text-[#003d45]">Governance overview</h2>
                <p className="mt-1 text-sm text-gray-500">Everything going on across the governance projects, and where staff can help.</p>
            </div>

            <div id="gov-staff-projects" className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                {PROJECTS.map((p) => (
                    <div key={p.name} className={`rounded-xl border p-4 ${p.status === "Live" ? "border-[#007a8a] bg-[#007a8a]/5" : "border-gray-200 bg-white"}`}>
                        <div className="flex items-center justify-between">
                            <span className="text-sm font-semibold text-[#003d45]">{p.name}</span>
                            <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${p.status === "Live" ? "bg-[#007a8a] text-white" : "bg-gray-100 text-gray-500"}`}>{p.status === "Live" ? "Live" : p.status === "Next" ? "Up next" : "Planned"}</span>
                        </div>
                        <p className="mt-1 text-xs text-gray-500">{p.note}</p>
                        {p.status === "Live" && <p className="mt-2 text-xs font-medium text-[#007a8a]">{groups.length} committees · {onTrack} on track</p>}
                    </div>
                ))}
            </div>

            <div id="gov-staff-stats" className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                <Stat label="Committees with 2027 planned" value={`${planned} of ${groups.length}`} />
                <Stat label="Meetings held this year" value={`${held} of ${due}`} hint="Of those that were due" />
                <Stat label="Minutes on time" value={held ? `${Math.round((onTime / held) * 100)}%` : "—"} hint="Confirmed within 72 hours" />
                <Stat label="Reports this quarter" value={`${reports} of ${groups.length}`} />
            </div>

            <div className="grid gap-6 lg:grid-cols-5">
                <Card id="gov-staff-help" className="lg:col-span-3">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-[#003d45]"><HandHelping className="size-4 text-[#007a8a]" />Where staff can help</CardTitle>
                        <CardDescription>Things a staff liaison can sort out for a committee.</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-2">
                        {help.map((h) => (
                            <div key={h.id} className={`flex flex-wrap items-center justify-between gap-3 rounded-lg border p-3 ${handled.includes(h.id) ? "border-gray-100 bg-gray-50" : "border-gray-200"}`}>
                                <div className="min-w-0 flex-1">
                                    <div className="text-sm font-semibold text-gray-800">{h.group}</div>
                                    <div className="text-xs text-gray-600">{h.text}</div>
                                </div>
                                {handled.includes(h.id) ? <span className="text-xs font-medium text-emerald-600">✓ Done</span>
                                    : <Button variant="outline" size="sm" onClick={() => setHandled([...handled, h.id])}>{h.action}</Button>}
                            </div>
                        ))}
                    </CardContent>
                </Card>

                <Card id="gov-staff-activity" className="lg:col-span-2">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-[#003d45]"><Activity className="size-4 text-[#007a8a]" />Recent activity</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <ol className="space-y-2.5">
                            {activity.slice(0, 8).map((a, i) => (
                                <li key={i} className="flex flex-col gap-0.5 text-sm sm:flex-row sm:gap-3">
                                    <span className="shrink-0 text-xs text-gray-400 sm:w-20">{a.at}</span>
                                    <span className="text-gray-700">{a.text}</span>
                                </li>
                            ))}
                        </ol>
                    </CardContent>
                </Card>
            </div>

            <Card id="gov-staff-groups">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-[#003d45]"><LayoutGrid className="size-4 text-[#007a8a]" />All committees</CardTitle>
                    <CardDescription>Click a committee for its meetings, policies and latest report. Chapters and communities sit under the Global Chapter Development Committee.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                    <div className="flex flex-wrap gap-4">
                        <Chips value={type} options={TYPES} onChange={setType} />
                        <Chips value={state} options={HEALTHS} onChange={setState} />
                    </div>
                    {/* Phones: one card per committee */}
                    <div className="space-y-2 md:hidden">
                        {shown.map((g) => (
                            <button key={g.id} onClick={() => setOpen(g)} className="w-full rounded-lg border border-gray-200 p-3 text-left">
                                <div className="flex items-start justify-between gap-2">
                                    <div className="min-w-0"><div className="font-medium text-gray-800">{g.name}</div><div className="text-xs text-gray-400">{g.type} · {chairOf(g)}{g.covers && <span className="block text-[#007a8a]">{coversLine(g)}</span>} · liaison {g.liaison}</div></div>
                                    <HealthBadge health={health(g)} />
                                </div>
                                <div className="mt-2 text-xs text-gray-600">{issues(g)[0] ?? "Nothing to do"}{issues(g).length > 1 && <span className="text-gray-400"> +{issues(g).length - 1} more</span>}</div>
                                <div className="mt-1 flex flex-wrap justify-between gap-2 text-xs text-gray-500">
                                    <span>{g.held} of {g.due} meetings held</span>
                                    {reminded.includes(g.id) && <span className="text-emerald-600">Reminded by the Governance Committee</span>}
                                </div>
                            </button>
                        ))}
                        {shown.length === 0 && <p className="py-6 text-center text-sm text-gray-400">No committees match.</p>}
                    </div>
                    <div className="hidden overflow-x-auto md:block">
                        <table className="w-full min-w-[720px] text-sm">
                            <thead><tr className="border-b border-gray-200 text-left text-xs text-gray-500"><th className="py-2 pr-3 font-medium">Committee</th><th className="pr-3 font-medium">Status</th><th className="pr-3 font-medium">What needs doing</th><th className="pr-3 font-medium">Held</th><th className="pr-3 font-medium">Staff liaison</th><th className="font-medium">Followed up</th></tr></thead>
                            <tbody>
                                {shown.map((g) => (
                                    <tr key={g.id} onClick={() => setOpen(g)} className="cursor-pointer border-b border-gray-100 align-top hover:bg-gray-50">
                                        <td className="py-2.5 pr-3"><div className="font-medium text-gray-800">{g.name}</div><div className="text-xs text-gray-400">{g.type} · {chairOf(g)}{g.covers && <span className="block text-[#007a8a]">{coversLine(g)}</span>}</div></td>
                                        <td className="pr-3 pt-2.5"><HealthBadge health={health(g)} /></td>
                                        <td className="pr-3 pt-2.5 text-xs text-gray-600">{issues(g)[0] ?? "—"}{issues(g).length > 1 && <span className="text-gray-400"> +{issues(g).length - 1} more</span>}</td>
                                        <td className="pr-3 pt-2.5 text-gray-600">{g.held} of {g.due}</td>
                                        <td className="pr-3 pt-2.5 text-gray-600">{g.liaison}</td>
                                        <td className="pt-2.5 text-xs">{reminded.includes(g.id) ? <span className="text-emerald-600">Reminded by the Governance Committee</span> : <span className="text-gray-300">—</span>}</td>
                                    </tr>
                                ))}
                                {shown.length === 0 && <tr><td colSpan={6} className="py-6 text-center text-gray-400">No committees match.</td></tr>}
                            </tbody>
                        </table>
                    </div>
                </CardContent>
            </Card>

            <Card id="gov-staff-switches" size="sm">
                <CardContent className="flex flex-wrap items-center justify-between gap-3">
                    <div className="text-sm text-gray-600">
                        <b className="text-gray-800">Pause for every committee</b> — for example during an outage. Nothing is lost.
                    </div>
                    <div className="flex gap-2">
                        {(["scheduling", "recording"] as const).map((k) => (
                            <Button key={k} variant="outline" size="sm" onClick={() => setPaused({ ...paused, [k]: !paused[k] })}>
                                {paused[k] ? <PlayCircle /> : <PauseCircle />}{paused[k] ? `Resume ${k}` : `Pause ${k}`}
                            </Button>
                        ))}
                    </div>
                </CardContent>
            </Card>

            <GroupDetail group={open} onClose={() => setOpen(null)} />
        </div>
    );
}

function Chips<T extends string>({ value, options, onChange }: { value: T; options: readonly T[]; onChange: (v: T) => void }) {
    return (
        <div className="flex flex-wrap gap-1">
            {options.map((o) => (
                <button key={o} type="button" onClick={() => onChange(o)}
                    className={`rounded-full border px-3 py-1 text-xs font-medium ${value === o ? "border-[#003d45] bg-[#003d45] text-white" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}>
                    {o}
                </button>
            ))}
        </div>
    );
}
